/**
 * Что человек уже видел в панели уведомлений — Блок 2 · Ф7 · В11-a.
 *
 * Серверного признака прочтения нет (колонки под него нет, DDL в фазе — у
 * релизной волны), поэтому память живёт в браузере. Отсюда свойство, названное
 * в подписи панели: на другом устройстве и в другом браузере она своя.
 *
 * Хранятся ТРИ вещи, каждая под ключом с идентификатором пользователя:
 *
 *   - **метка** — `asOf` ответа сервера: «видел всё до этого момента»;
 *   - **показанные строки моложе метки** — их идентификаторы. Метка отстаёт
 *     от выборки на запас (`NOTIFICATION_FEED_AS_OF_MARGIN_SECONDS`), а момент
 *     строки бывает и позже часов базы (ручная вставка в чужой шкале,
 *     переключённый пояс сессии — долг #223). Без этого списка такая строка
 *     оставалась бы «новой», сколько бы раз панель ни открывали;
 *   - **скрытые прочитанные** (В11-e) — строки, убранные из панели кнопкой
 *     «Скрыть прочитанные». Устройство и правила — в разделе ниже по файлу;
 *     чтение и запись у них свои, не `read`/`write` метки.
 *
 * ⚠️ МЕТКА — `asOf` ОТВЕТА СЕРВЕРА, НИКОГДА `Date.now()`. Часы браузера серверу
 * не ровня: отстающие сделали бы всё вечно новым, спешащие — пометили бы
 * виденным то, чего человек не видел.
 *
 * ⚠️ `userId` В КЛЮЧЕ — НЕСУЩЕЕ: вкладка переживает смену учётной записи, и
 * общий ключ отдал бы второму человеку метку первого.
 *
 * ⚠️ ХРАНИЛИЩЕ МОЖЕТ БРОСАТЬ (приватное окно, запрет сайтовых данных). Тогда
 * память живёт во вкладке: счётчик работает до перезагрузки, после неё
 * начинает с нуля — «без метки новых нет», а не «всё новое».
 */
import { NOTIFICATION_FEED_WINDOW_DAYS } from '@aris/shared';

const AS_OF_PREFIX = 'aris.notifications.seenAsOf.';
const IDS_PREFIX = 'aris.notifications.seenIds.';

const memory = new Map<string, string>();

function read(key: string): string | null {
  try {
    const stored = window.localStorage.getItem(key);
    if (stored !== null) return stored;
  } catch {
    // Хранилище недоступно — ниже запасной путь.
  }
  return memory.get(key) ?? null;
}

function write(key: string, value: string): void {
  memory.set(key, value);
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Значение осталось в памяти вкладки.
  }
}

/** Ключ хранилища метки — для подписки на `storage` из соседней вкладки. */
export function seenAsOfStorageKey(userId: string): string {
  return `${AS_OF_PREFIX}${userId}`;
}

/** Ключ хранилища показанных строк. */
export function seenIdsStorageKey(userId: string): string {
  return `${IDS_PREFIX}${userId}`;
}

export function readSeenAsOf(userId: string): string | null {
  return read(seenAsOfStorageKey(userId));
}

/** Разобрать сохранённый список; мусор в хранилище — пустой список, не бросок. */
export function parseSeenIds(raw: string | null): ReadonlySet<string> {
  if (raw === null) return new Set();
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    return new Set();
  }
}

export function readSeenIds(userId: string): ReadonlySet<string> {
  return parseSeenIds(read(seenIdsStorageKey(userId)));
}

/**
 * Записать «видел»: метку и строки моложе неё, показанные человеку.
 *
 * Список ЗАМЕНЯЕТСЯ, а не дополняется: строки старше новой метки покрыты ею
 * самой, и копить их незачем. Отдельного потолка длины нет — он был бы
 * недостижим: в список идут только строки первой страницы ленты, а её размер
 * ограничен договором.
 */
export function writeSeen(userId: string, asOf: string, shownIds: readonly string[]): void {
  write(seenAsOfStorageKey(userId), asOf);
  write(seenIdsStorageKey(userId), JSON.stringify(shownIds));
}

/**
 * Забыть показанные строки ВЫШЕДШЕГО пользователя — на выходе.
 *
 * ⚠️ МЕТКА «ВИДЕЛ ДО» ВЫХОД ПЕРЕЖИВАЕТ, и это исправление, а не недосмотр.
 * Фикс-пак 1 стирал на выходе всю память панели — и каждый, кто нажимает
 * «Выйти» вечером, утром получал «первый заход»: счётчик ноль на всё, что
 * пришло за ночь (приёмка da, Н-1). Метка — время, а не содержание; оставшись
 * в браузере, она говорит лишь «этот человек смотрел панель тогда-то».
 *
 * ⚠️ СТИРАЕТСЯ ПАМЯТЬ ТОЛЬКО ЭТОГО ПОЛЬЗОВАТЕЛЯ: чужие ключи того же браузера
 * выход не трогает.
 *
 * Без списка показанных строка из запаса метки после входа один раз покажется
 * «новой» повторно — цена малая и названная.
 */
export function clearShownNotificationIds(userId: string): void {
  const key = seenIdsStorageKey(userId);
  memory.delete(key);
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Выход не должен падать из-за хранилища.
  }
}

// ─── Скрытые прочитанные (В11-e) ──────────────────────────────────────────

/**
 * Что человек убрал из панели кнопкой «Скрыть прочитанные».
 *
 * Решение владельца 08.10.2026, вариант Б: скрытие живёт в браузере — на
 * другом устройстве строки остаются. Сервер о нём не знает.
 *
 * ⚠️ ХРАНИТСЯ МНОЖЕСТВО ПОКАЗАННЫХ СТРОК, А НЕ ПРАВИЛО. Скрыта может быть лишь
 * строка, которая в миг нажатия была ЗАГРУЖЕНА в панель и стояла в ней без
 * отметки «новое». Первая редакция хранила границу по времени («всё не новее
 * метки „видел до“») — и прятала то, чего человек не видел ни разу: вторую
 * страницу при «20+» новых, все 30 дней на новом устройстве, строку, пришедшую
 * после нажатия с моментом не позже границы (фикс-пак 1: da Н-1 и Н-2, первая
 * находка Codex). Граница по времени не различает «видел» и «мог бы увидеть»;
 * идентификатор — различает. Отсюда без отдельных оговорок:
 *
 *   - строки глубже загруженного не скрыты и достижимы «Показать ещё»;
 *   - строка с тем же моментом, что у скрытой, и строка, появившаяся в ленте
 *     позже с любым моментом, видны: их в множестве нет;
 *   - испорченное значение в хранилище может спрятать только названные в нём
 *     строки — «всё навсегда» им не выразить.
 *
 * ⚠️ ОБЪЁМ. С идентификатором лежит момент строки (мс). При каждой записи
 * выбрасываются записи вне окна: старше `NOTIFICATION_FEED_WINDOW_DAYS` (в
 * ленте такой строки уже нет) и позже серверного «сейчас» больше чем на
 * `HIDDEN_FUTURE_MARGIN_MS` (иначе запись с моментом из далёкого будущего не
 * вычищалась бы никогда и вечно занимала место под потолком). «Сейчас» — `asOf`
 * ответа сервера, не часы браузера. Сверх того потолок `HIDDEN_MAX`; при
 * переполнении теряются самые СТАРЫЕ по моменту — они ближе всего к выпадению
 * из ленты, а недавно скрытое остаётся скрытым. Потерянная запись = строка
 * снова видна: отказ в сторону «показать», а не «потерять».
 *
 * ⚠️ КТО ГЛАВНЕЕ — ХРАНИЛИЩЕ ИЛИ ПАМЯТЬ ВКЛАДКИ. Правило одно (для ключа
 * скрытия; метка «видел» живёт по прежним `read`/`write` выше и здесь не
 * тронута), и оно в одной функции — `currentHidden`:
 *
 *   1. главное — хранилище: его видят все вкладки. Оно ПЕРЕЧИТЫВАЕТСЯ при
 *      каждом обращении — в том числе по событию `storage`: полезная нагрузка
 *      события бывает старше того, что уже лежит в хранилище, и ей не верят;
 *   2. если последняя запись ЭТОЙ вкладки не удалась (квота, запрет), её
 *      несохранённое значение действует ПОВЕРХ хранилища:
 *        - несохранённое скрытие объединяется с тем, что лежит в хранилище, —
 *          и при чтении, и при следующей записи. Запись соседней вкладки при
 *          этом не затирается, своё не теряется;
 *        - несохранённое «скрытие снято» держится, пока хранилище не
 *          изменилось с момента неудачи (там лежит то самое значение, которое
 *          не удалось удалить). Изменилось — значит, соседняя вкладка скрыла
 *          что-то уже после; действует её запись, а несохранённое забывается
 *          насовсем — с первого же замеченного расхождения;
 *   3. удачная запись снимает несохранённое значение;
 *   4. хранилище не читается вовсе (бросает) — память вкладки.
 *
 * ⚠️ «ПЕРЕЧИТАТЬ, ОБЪЕДИНИТЬ, ЗАПИСАТЬ» — НЕ АТОМАРНО, и у `localStorage`
 * атомарной операции нет. Две вкладки, нажавшие кнопку в одно мгновение, могут
 * обе прочитать старое значение, и запись первой пропадёт. Вред — в сторону
 * «показать»: часть скрытого одной вкладкой снова видна, лечится повторным
 * нажатием.
 */
export type HiddenEntry = readonly [id: string, occurredAtMs: number];

/** Потолок числа записей — с запасом на всю ленту активного человека. */
export const HIDDEN_MAX = 500;

const HIDDEN_PREFIX = 'aris.notifications.hiddenRead.';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DAY_MS = 86_400_000;

/** Насколько момент записи может опережать серверное «сейчас» (долг #223 — часы, не сутки). */
export const HIDDEN_FUTURE_MARGIN_MS = DAY_MS;

/**
 * Что эта вкладка не смогла записать: `value === null` — не смогла удалить.
 * `staleRaw` — что лежало в хранилище в момент неудачи.
 */
const unsaved = new Map<string, { value: string | null; staleRaw: string | null }>();

function writeHiddenRaw(key: string, value: string | null): void {
  if (value === null) memory.delete(key);
  else memory.set(key, value);
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
    unsaved.delete(key);
  } catch {
    try {
      unsaved.set(key, { value, staleRaw: window.localStorage.getItem(key) });
    } catch {
      // Хранилище не читается вовсе — действует память вкладки (правило 4).
      unsaved.delete(key);
    }
  }
}

/** Ключ хранилища скрытия — для подписки на `storage` из соседней вкладки. */
export function hiddenReadStorageKey(userId: string): string {
  return `${HIDDEN_PREFIX}${userId}`;
}

function isHiddenEntry(value: unknown): value is HiddenEntry {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === 'string' &&
    UUID.test(value[0]) &&
    typeof value[1] === 'number' &&
    Number.isSafeInteger(value[1])
  );
}

/**
 * Разобрать сохранённое скрытие. Чужой формат — «ничего не скрыто»; запись не
 * той формы (не идентификатор, не момент) отбрасывается поштучно.
 */
export function parseHidden(raw: string | null): readonly HiddenEntry[] {
  if (raw === null) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isHiddenEntry).slice(0, HIDDEN_MAX);
  } catch {
    return [];
  }
}

/** Привести множество к хранимому виду: без повторов, от новых к старым, под потолок. */
function normalize(entries: Iterable<HiddenEntry>): readonly HiddenEntry[] {
  return [...new Map(entries)]
    .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? 1 : a[0] > b[0] ? -1 : 0))
    .slice(0, HIDDEN_MAX);
}

/** Действующее скрытие по ключу — единственное место правила главенства. */
function currentHidden(key: string): readonly HiddenEntry[] {
  let stored: string | null;
  try {
    stored = window.localStorage.getItem(key);
  } catch {
    return parseHidden(memory.get(key) ?? null);
  }
  const pending = unsaved.get(key);
  if (pending === undefined) return parseHidden(stored);
  if (stored === pending.staleRaw) return parseHidden(pending.value);
  // Хранилище изменилось после неудачной записи — соседняя вкладка записала своё.
  if (pending.value === null) {
    // Несохранённое «скрытие снято» на этом КОНЧАЕТСЯ, а не засыпает: иначе
    // соседняя вкладка, позже записавшая ровно прежнее значение, выглядела бы
    // как «хранилище не менялось», и её скрытие здесь не действовало бы.
    unsaved.delete(key);
    return parseHidden(stored);
  }
  return normalize([...parseHidden(stored), ...parseHidden(pending.value)]);
}

/** Действующее скрытие пользователя. Зовётся и по событию `storage` — см. правило 1. */
export function readHidden(userId: string): readonly HiddenEntry[] {
  return currentHidden(hiddenReadStorageKey(userId));
}

/**
 * Следующее множество: прежнее плюс показанные сейчас прочитанные строки.
 *
 * Порядок — от новых к старым; записи вне окна (старше ленты либо позже
 * серверного «сейчас» с запасом) выбрасываются, потолок срезает самые старые.
 */
export function mergeHidden(
  previous: readonly HiddenEntry[],
  shown: readonly { id: string; occurredAt: string }[],
  nowMs: number,
): readonly HiddenEntry[] {
  const added: HiddenEntry[] = [];
  for (const item of shown) {
    const at = Date.parse(item.occurredAt);
    if (!Number.isNaN(at)) added.push([item.id, at]);
  }
  const floor = nowMs - NOTIFICATION_FEED_WINDOW_DAYS * DAY_MS;
  const ceiling = nowMs + HIDDEN_FUTURE_MARGIN_MS;
  return normalize([...previous, ...added].filter(([, at]) => !(at < floor) && !(at > ceiling)));
}

/**
 * «Скрыть прочитанные»: дописать показанные строки к действующему множеству.
 *
 * ⚠️ МНОЖЕСТВО БЕРЁТСЯ ИЗ ХРАНИЛИЩА, а не из состояния компонента: соседняя
 * вкладка могла нажать ту же кнопку, а её событие `storage` сюда ещё не дошло.
 * Запись «своего снимка» стёрла бы её строки.
 */
export function hideShownRead(
  userId: string,
  shown: readonly { id: string; occurredAt: string }[],
  serverAsOf: string,
): readonly HiddenEntry[] {
  const key = hiddenReadStorageKey(userId);
  const next = mergeHidden(currentHidden(key), shown, Date.parse(serverAsOf));
  writeHiddenRaw(key, JSON.stringify(next));
  return next;
}

/** «Показать скрытые»: скрытие снимается целиком. */
export function clearHidden(userId: string): void {
  writeHiddenRaw(hiddenReadStorageKey(userId), null);
}

/** Только для тестов: забыть память вкладки. */
export function __resetSeenForTests(): void {
  memory.clear();
  unsaved.clear();
}
